import axios from "axios";
import { baseUrl } from "../../resources/Constants";
import sessionManager from "../../utils/sessionManager";

const instance = axios.create({
    baseURL: baseUrl,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

// ✅ INTERCEPTOR DE REQUEST: Agregar token automáticamente
instance.interceptors.request.use(
    (config) => {
        const url = config.url || '';
        const esSuperadmin = url.includes('/superadmin/');
        const superadminToken = localStorage.getItem('superadmin_token');

        if (esSuperadmin && superadminToken) {
            config.headers.Authorization = `Bearer ${superadminToken}`;
        } else {
            const userdata = localStorage.getItem('userdata');
            if (userdata) {
                try {
                    const { access_token } = JSON.parse(userdata);
                    if (access_token) {
                        config.headers.Authorization = `Bearer ${access_token}`;
                    }
                } catch (error) {
                    console.error('Error parsing userdata:', error);
                    localStorage.removeItem('userdata');
                }
            }
        }

        // Manual CSRF Token Injection
        const getCookie = (name) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
        }

        const xsrfToken = getCookie('XSRF-TOKEN');
        if (xsrfToken) {
            config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ✅ INTERCEPTOR DE RESPONSE: Manejar errores 401 (token expirado) y 403 (acceso denegado)
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        // 🔒 MANEJAR ERROR 403 (Acceso Denegado)
        if (error.response?.status === 403) {
            const suppressModal = error.config?.headers?.['X-Suppress-403-Modal'];
            const code = error.response.data?.code;
            const esDenegacionClinicaAdmin = code === 'ADMIN_NO_CLINICO';

            if (!suppressModal && !esDenegacionClinicaAdmin) {
                // Importar Swal dinámicamente para evitar problemas de dependencias circulares
                import('sweetalert2').then((Swal) => {
                    Swal.default.fire({
                        icon: 'error',
                        title: 'Acceso Denegado',
                        text: error.response.data.message || 'No tiene permisos para acceder a este recurso',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#d33',
                        allowOutsideClick: false
                    });
                });
            }
            
            // Log para auditoría
            console.warn('🔒 Intento de acceso denegado:', {
                url: error.config?.url,
                method: error.config?.method,
                timestamp: new Date().toISOString(),
                message: error.response.data.message,
                suppressed: !!suppressModal
            });
        }
        
        // Solo manejar token expirado si:
        // 1. Es un error 401
        // 2. El mensaje indica token expirado o inválido
        // 3. Hay un token en localStorage (usuario estaba autenticado)
        if (error.response?.status === 401) {
            const userdata = localStorage.getItem('userdata');
            const errorMessage = error.response?.data?.message || '';
            
            // Solo cerrar sesión si había un usuario autenticado
            // y el error es específicamente de token
            if (userdata && (
                errorMessage.toLowerCase().includes('unauthenticated') ||
                errorMessage.toLowerCase().includes('token') ||
                errorMessage.toLowerCase().includes('expired')
            )) {
                // Limpiar localStorage
                localStorage.removeItem('userdata');
                
                // Notificar al sistema de sesión expirada
                sessionManager.notifySessionExpired();
                
                // Redirigir al login después de un breve delay
                setTimeout(() => {
                    window.location.href = '/';
                }, 2000);
            }
        }
        return Promise.reject(error);
    }
);

export default instance;